import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, RotateCcw, Shuffle } from "lucide-react";

interface Flashcard {
  id: string;
  question: string;
  answer: string;
}

interface FlashcardViewerProps {
  cards: Flashcard[];
  title: string;
}

const FlashcardViewer = ({ cards, title }: FlashcardViewerProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [shuffledCards, setShuffledCards] = useState(cards);

  const currentCard = shuffledCards[currentIndex];

  const handleNext = () => {
    setIsFlipped(false);
    setCurrentIndex((prev) => (prev + 1) % shuffledCards.length);
  };

  const handlePrev = () => {
    setIsFlipped(false);
    setCurrentIndex((prev) => (prev - 1 + shuffledCards.length) % shuffledCards.length);
  };

  const handleShuffle = () => {
    const shuffled = [...shuffledCards].sort(() => Math.random() - 0.5);
    setShuffledCards(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleReset = () => {
    setShuffledCards(cards);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  if (!currentCard) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No flashcards available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{title}</h2>
        <span className="text-sm text-muted-foreground">
          {currentIndex + 1} / {shuffledCards.length}
        </span>
      </div>

      {/* Flashcard */}
      <div
        className="relative w-full h-64 md:h-80 cursor-pointer perspective-1000"
        onClick={() => setIsFlipped(!isFlipped)}
      >
        <div
          className={`relative w-full h-full transition-transform duration-500 transform-style-preserve-3d ${
            isFlipped ? "rotate-y-180" : ""
          }`}
        >
          {/* Front - Question */}
          <div className="absolute inset-0 w-full h-full backface-hidden">
            <div className="w-full h-full rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 flex flex-col items-center justify-center p-6 text-center">
              <span className="text-xs uppercase tracking-wider text-primary/60 mb-4">Question</span>
              <p className="text-lg md:text-xl font-medium text-foreground">{currentCard.question}</p>
              <span className="text-xs text-muted-foreground mt-6">Click to reveal answer</span>
            </div>
          </div>

          {/* Back - Answer */}
          <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180">
            <div className="w-full h-full rounded-2xl bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20 flex flex-col items-center justify-center p-6 text-center">
              <span className="text-xs uppercase tracking-wider text-accent/60 mb-4">Answer</span>
              <p className="text-lg md:text-xl font-medium text-foreground">{currentCard.answer}</p>
              <span className="text-xs text-muted-foreground mt-6">Click to see question</span>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="outline" size="icon" aria-label="Previous card" onClick={handlePrev}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <Button variant="outline" size="icon" aria-label="Reset card order" onClick={handleReset}>
          <RotateCcw className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="icon" aria-label="Shuffle cards" onClick={handleShuffle}>
          <Shuffle className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="icon" aria-label="Next card" onClick={handleNext}>
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-1 flex-wrap max-w-md mx-auto">
        {shuffledCards.map((_, idx) => (
          <button
            key={idx}
            aria-label={`Go to card ${idx + 1}`}
            onClick={() => {
              setCurrentIndex(idx);
              setIsFlipped(false);
            }}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentIndex ? "bg-primary w-4" : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default FlashcardViewer;
